/*
 * Date and time for display. Everything shown to the user is formatted
 * for the picked UI language, so the same value never reads as
 * "07/25/2026" in a German session.
 *
 * A point in time is always shown in the timezone of the browser — the
 * one the reader lives in. What the server sends decides how it is
 * read: with a zone ("…T08:27:24+02:00", "…Z") that zone counts, and
 * without one the value is taken as UTC, which is what the backend
 * stores. Guessing "local" for a zoneless value would silently shift
 * every timestamp by the offset of whoever is looking.
 */

/*
 * A day without a time of its own ("2026-07-25"): parsed as a local
 * date on purpose. `new Date('2026-07-25')` means midnight UTC, which
 * west of Greenwich renders as the day before — for a calendar day
 * that is simply wrong.
 */
export function formatDay(isoDay: string, locale?: string): string {
  const [year, month, day] = String(isoDay).split('-').map(Number);
  if (!year || !month || !day) return isoDay;

  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* the calendar day it is right now, as the ISO day the backend stores —
   read from the local clock, not from UTC: shortly after midnight the
   two disagree, and "today" means the day on the reader's wall */
export function todayIsoDay(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

/*
 * How a calendar day is written down — a setting, not a guess.
 *
 * "Automatic" follows the UI language, which is what everything else
 * here does. It is not always what the reader wants: a browser set to
 * English writes 09/18/1967 for a German session's date input, and a
 * date in the wrong order is not a matter of taste but a misreading
 * waiting to happen. So the two unambiguous spellings can be picked
 * outright (see the configuration page).
 */
export const DATE_FORMAT_AUTO = 'auto';
export const DATE_FORMAT_DMY = 'dmy';
export const DATE_FORMAT_ISO = 'iso';
export const DATE_FORMATS = [
  DATE_FORMAT_AUTO,
  DATE_FORMAT_DMY,
  DATE_FORMAT_ISO,
] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

/* a calendar day as the backend writes it, and nothing else */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/* the same day, typed the way `dmy` spells it — one or two digits for
   day and month, because that is how people type */
const TYPED_DMY = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/;

const isRealDay = (year: number, month: number, day: number) => {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

/*
 * A calendar day for reading. Anything that is not one is handed back
 * untouched — a table cell holds whatever the row holds.
 */
export function formatCalendarDay<T>(
  value: T,
  dateFormat?: DateFormat,
  locale?: string
): string | T {
  const match = ISO_DAY.exec(String(value ?? '').trim());
  if (!match) return value;

  const [, year, month, day] = match;
  if (dateFormat === DATE_FORMAT_ISO) return `${year}-${month}-${day}`;
  if (dateFormat === DATE_FORMAT_DMY) return `${day}.${month}.${year}`;

  /* built from local parts on purpose, see formatDay */
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day)
  ).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/*
 * A day as someone typed it, back into what the backend stores — or
 * null where it is not (yet) a day. Only the picked spelling is read:
 * accepting "03.04.2026" as April in an ISO session would guess at
 * exactly the ambiguity the setting exists to remove.
 */
export function parseCalendarDay(
  text: string | null | undefined,
  dateFormat?: DateFormat
): string | null {
  const typed = String(text ?? '').trim();
  if (!typed) return null;

  const pattern = dateFormat === DATE_FORMAT_DMY ? TYPED_DMY : ISO_DAY;
  const match = pattern.exec(typed);
  if (!match) return null;

  const [year, month, day] =
    dateFormat === DATE_FORMAT_DMY
      ? [Number(match[3]), Number(match[2]), Number(match[1])]
      : [Number(match[1]), Number(match[2]), Number(match[3])];

  /* the 31st of February parses but is no day */
  if (!isRealDay(year, month, day)) return null;

  const pad = (number: number) => String(number).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/* a date with a time of day, the separator either 'T' or a space, the
   seconds, the fraction and the zone each optional */
const TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

/*
 * The instant behind a value, or null for anything that carries no time
 * of day — a plain calendar day belongs to `formatDay`, not here.
 * Numbers are milliseconds since the epoch, as `Date` reads them.
 */
export function parseTimestamp(value: unknown): Date | null {
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }
  if (typeof value !== 'string') return null;

  const match = TIMESTAMP.exec(value.trim());
  if (!match) return null;

  const [, year, month, day, hour, minute, second, zone] = match;
  const date = new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second || '00'}${zone || 'Z'}`
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

/* the instant in the timezone of the browser; `timeZone` overrides that
   and exists so tests do not depend on where they run */
export function formatTimestamp<T>(
  value: T,
  locale?: string,
  timeZone?: string
): string | T {
  const date = parseTimestamp(value);
  if (!date) return value;

  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/*
 * The day spelled out with the time of day but without the seconds —
 * for a moment someone wrote down rather than a machine recorded, where
 * a trailing ":00" would only claim a precision that is not there.
 */
export function formatDayTime<T>(
  value: T,
  locale?: string,
  timeZone?: string
): string | T {
  const date = parseTimestamp(value);
  if (!date) return value;

  return date.toLocaleString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/*
 * The two halves of `formatDayTime`, for a list that groups its entries
 * under the day they fall on and prints only the time on each of them.
 *
 * The day has to be derived from the instant as well, not from the
 * calendar date the value was written with: read far enough east, a
 * late-evening entry belongs to the next day, and a heading taken from
 * the written date would then stand above a time that contradicts it.
 */
export function formatInstantDay<T>(
  value: T,
  locale?: string,
  timeZone?: string
): string | T {
  const date = parseTimestamp(value);
  if (!date) return value;

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
}

export function formatTime<T>(
  value: T,
  locale?: string,
  timeZone?: string
): string | T {
  const date = parseTimestamp(value);
  if (!date) return value;

  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/*
 * For values whose type is not known beforehand — a table cell may hold
 * a timestamp, a calendar day, a name or a number. Only a string that
 * really is one of the two is rewritten; everything else passes
 * through untouched.
 */
export function localizeTimestamp<T>(
  value: T,
  locale?: string,
  dateFormat?: DateFormat
): string | T {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (TIMESTAMP.test(trimmed)) return formatTimestamp(value, locale);
  if (ISO_DAY.test(trimmed))
    return formatCalendarDay(trimmed, dateFormat, locale);
  return value;
}
